import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import MatchChatBox from '../components/MatchChatBox';
import FormField, { Input } from '../components/FormField';
import { getRoundLabel } from '../utils/bracketUtils';

const STATUS_LABELS = {
  scheduled: 'Offen',
  pending_confirmation: 'Bestätigung ausstehend',
  completed: 'Abgeschlossen',
  bye: 'BYE',
};
const STATUS_STYLES = {
  scheduled: 'bg-slate-50 text-slate-500 border-slate-200',
  pending_confirmation: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  bye: 'bg-slate-50 text-slate-400 border-slate-200',
};

function TeamCard({ team, score, isWinner, matchStatus }) {
  const navigate = useNavigate();
  if (!team) return (
    <div className="flex-1 border-2 border-dashed border-slate-200 rounded-xl p-4 text-center text-slate-300 text-sm">
      TBD
    </div>
  );
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/teams/${team.id}`)}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/teams/${team.id}`); }}
      className={`flex-1 border-2 rounded-xl p-4 transition-all hover:shadow-md cursor-pointer
      ${isWinner ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-ocean-300'}
    `}>
      <div className="flex items-center justify-between">
        <span className={`font-bold text-lg ${isWinner ? 'text-emerald-700' : 'text-slate-800'}`}>
          {team.name}
        </span>
        {matchStatus === 'completed' && (
          <span className={`text-3xl font-black ${isWinner ? 'text-emerald-600' : 'text-slate-400'}`}>
            {score ?? '–'}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {(team.members || []).filter((m) => m.status === 'confirmed').map((m) => (
          <Link
            key={m.id}
            to={`/users/${m.user_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-600 hover:bg-ocean-50 hover:text-ocean-700 hover:border-ocean-300 transition-colors"
          >
            {m.user?.nickname || m.user?.username}
          </Link>
        ))}
      </div>
      {isWinner && <div className="text-xs font-bold text-emerald-600 mt-2">Gewinner</div>}
    </div>
  );
}

export default function MatchDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [team1, setTeam1] = useState(null);
  const [team2, setTeam2] = useState(null);
  const [finalRound, setFinalRound] = useState(0);
  const [loading, setLoading] = useState(true);
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    api.get(`/api/matches/${id}`)
      .then(async (r) => {
        setMatch(r.data);
        const tournamentId = r.data.tournament_id;
        const promises = [];
        if (r.data.team1_id) promises.push(api.get(`/api/teams/${r.data.team1_id}`));
        else promises.push(Promise.resolve(null));
        if (r.data.team2_id) promises.push(api.get(`/api/teams/${r.data.team2_id}`));
        else promises.push(Promise.resolve(null));
        if (tournamentId) promises.push(api.get(`/api/tournaments/${tournamentId}/bracket`));
        else promises.push(Promise.resolve(null));
        const [t1, t2, bracketRes] = await Promise.all(promises);
        setTeam1(t1?.data || null);
        setTeam2(t2?.data || null);
        if (bracketRes?.data?.matches) {
          const rounds = bracketRes.data.matches
            .filter((m) => m.bracket_side === 'winners')
            .map((m) => m.round);
          setFinalRound(rounds.length > 0 ? Math.max(...rounds) : 0);
        }
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const isCreatorOf = (team) => user && team && team.creator_id === user.id;
  const isMemberOf = (team) => user && team && (team.members || []).some(
    (m) => m.user_id === user.id && m.status === 'confirmed'
  );

  const isTeam1Creator = isCreatorOf(team1);
  const isTeam2Creator = isCreatorOf(team2);
  const isAnyCreator = isTeam1Creator || isTeam2Creator;
  const isMember = isMemberOf(team1) || isMemberOf(team2);

  const submitResult = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      await api.post(`/api/matches/${id}/result`, {
        score_team1: parseInt(score1),
        score_team2: parseInt(score2),
      });
      setMessage('Ergebnis eingereicht – warte auf Bestätigung des Gegners.');
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setSubmitting(false); }
  };

  const confirm = async () => {
    setConfirming(true); setError('');
    try {
      await api.post(`/api/matches/${id}/confirm`);
      setMessage('Ergebnis bestätigt!');
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setConfirming(false); }
  };

  const reject = async () => {
    setRejecting(true); setError('');
    try {
      await api.post(`/api/matches/${id}/reject`);
      setMessage('Ergebnis abgelehnt. Das Ergebnis wurde zurückgesetzt.');
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setRejecting(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" />
    </div>
  );

  if (!match) return null;

  const isBye = match.status === 'bye';
  const isCompleted = match.status === 'completed';
  const isPending = match.status === 'pending_confirmation';
  const isScheduled = match.status === 'scheduled';

  const team1IsWinner = isCompleted && match.winner_id === match.team1_id;
  const team2IsWinner = isCompleted && match.winner_id === match.team2_id;

  // Who submitted: if result_submitted_by is creator of team1, team2 needs to confirm
  const submittedByTeam1Creator = team1 && match.result_submitted_by === team1.creator_id;
  const canConfirmOrReject = isPending && (
    (submittedByTeam1Creator && isTeam2Creator) ||
    (!submittedByTeam1Creator && isTeam1Creator)
  );

  return (
    <div className="max-w-3xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            {getRoundLabel(match.round, match.bracket_side, finalRound)}
          </span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${STATUS_STYLES[match.status] || ''}`}>
            {STATUS_LABELS[match.status] || match.status}
          </span>
        </div>
      </div>

      {message && <div className="alert-success mb-4">{message}</div>}
      {error && <div className="alert-error mb-4">{error}</div>}

      {/* Match display */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <TeamCard team={team1} score={match.score_team1} isWinner={team1IsWinner} matchStatus={match.status} />
        <div className="text-center min-w-[60px]">
          <div className="text-2xl font-black text-slate-300">vs</div>
          {isBye && <div className="text-xs text-slate-400 mt-1">BYE</div>}
        </div>
        <TeamCard team={team2} score={match.score_team2} isWinner={team2IsWinner} matchStatus={match.status} />
      </div>

      {isCompleted && match.result_by_admin && (
        <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 mb-6 w-fit">
          <span className="font-bold bg-red-500 text-white px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wide">Admin</span>
          <span>Ergebnis wurde von einem Admin eingetragen</span>
        </div>
      )}

      {/* Result actions */}
      {!isBye && !isCompleted && (
        <div className="card mb-6">
          {isScheduled && isAnyCreator && (
            <>
              <h3 className="section-title mb-3">Ergebnis eintragen</h3>
              <form onSubmit={submitResult}>
                <div className="flex items-center gap-4 flex-wrap mb-4">
                  <FormField label={team1?.name || 'Team 1'} className="flex-1 min-w-[120px]">
                    <Input
                      type="number" min="0" max="99"
                      value={score1}
                      onChange={(e) => setScore1(e.target.value)}
                      required placeholder="0"
                    />
                  </FormField>
                  <div className="text-slate-300 font-bold text-xl mt-4">:</div>
                  <FormField label={team2?.name || 'Team 2'} className="flex-1 min-w-[120px]">
                    <Input
                      type="number" min="0" max="99"
                      value={score2}
                      onChange={(e) => setScore2(e.target.value)}
                      required placeholder="0"
                    />
                  </FormField>
                </div>
                <Button type="submit" loading={submitting}>Ergebnis einreichen</Button>
              </form>
            </>
          )}
          {isPending && (
            <div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-sm text-amber-700">
                <strong>Eingereicht:</strong> {match.score_team1} : {match.score_team2}
              </div>
              {canConfirmOrReject ? (
                <div>
                  <p className="text-sm text-slate-600 mb-3">Bitte bestätige das Ergebnis oder lehne es ab:</p>
                  <div className="flex gap-3">
                    <Button variant="success" onClick={confirm} loading={confirming}>Bestätigen</Button>
                    <Button variant="danger" onClick={reject} loading={rejecting}>Ablehnen</Button>
                  </div>
                </div>
              ) : isAnyCreator ? (
                <p className="text-sm text-slate-400">Warte auf Bestätigung durch den Gegner.</p>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Chat */}
      <div className="mb-2">
        <h3 className="section-title mb-3">Match-Chat</h3>
        <MatchChatBox matchId={id} canWrite={isMember} />
      </div>
    </div>
  );
}
